import {
  EAlternateLevels,
  EColor,
  EFontWeight,
  ERouting,
  ESize,
  ETypographyVariant,
  LevelsLinkProps,
} from '@core/type';
import { ArticleContent } from '@core/type/api';

import { Accordion } from '../accordion';
import { Box } from '../layout';
import { LinkTranslatedStyledAsLink } from '../link-translated';
import { Typography } from '../typography';
import { LevelsProps } from './interface-levels';

const makeLinkProps = ({ slug, subSlug, subSubSlug, level }: LevelsLinkProps) => {
  switch (level) {
    case EAlternateLevels.LEVEL1:
      return {
        keyRoute: ERouting.PRECIOUS_METAL_GUIDE_LEVEL1,
        query: { slug },
      };
    case EAlternateLevels.LEVEL2:
      return {
        keyRoute: ERouting.PRECIOUS_METAL_GUIDE_LEVEL2,
        query: { slug, subSlug },
      };
    case EAlternateLevels.LEVEL3:
      return {
        keyRoute: ERouting.PRECIOUS_METAL_GUIDE_LEVEL3,
        query: { slug, subSlug, subSubSlug },
      };
    default:
      return { keyRoute: ERouting.PRECIOUS_METAL_GUIDE };
  }
};

const ArticleLink = ({
  title,
  isActive,
  ...linkProps
}: LevelsLinkProps & {
  title: string;
  isActive: boolean;
}) => (
  <LinkTranslatedStyledAsLink
    {...makeLinkProps({ ...linkProps })}
    fontWeight={EFontWeight.MEDIUM}
    variant={ETypographyVariant.BODY3}
    color={isActive ? EColor.ACCENT : EColor.SECONDARY}
  >
    {title}
  </LinkTranslatedStyledAsLink>
);

export const Levels = ({
  levels,
  route,
  activeSlug,
}: {
  levels: LevelsProps;
  route: ERouting;
  activeSlug?: string;
}) => {
  const { menuTitle: title, subtitle, children } = levels;

  const checkIfActive = (level1: ArticleContent) =>
    level1.slug === activeSlug ||
    level1.children.some(
      (level2) =>
        level2.slug === activeSlug || level2.children.some((level3) => level3.slug === activeSlug),
    );

  return (
    <Box>
      <Box marginBottom={'32px'}>
        <LinkTranslatedStyledAsLink
          keyRoute={route}
          variant={ETypographyVariant.BODY1}
          fontWeight={EFontWeight.BOLD}
        >
          {title}
        </LinkTranslatedStyledAsLink>

        {subtitle && (
          <Typography marginTop={'8px'} variant={ETypographyVariant.BODY3} color={EColor.SECONDARY}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {children?.map((level1) => (
        <Accordion
          key={level1.slug}
          titleContent={
            <ArticleLink
              slug={level1.slug}
              title={level1.menuTitle}
              isActive={activeSlug === level1.slug}
              level={EAlternateLevels.LEVEL1}
            />
          }
          iconSize={ESize.SM}
          iconHoverColor={EColor.NEUTRAL}
          iconBoxSize={44}
          marginBottom={'4px'}
          titleBgColor={EColor.SECONDARY}
          titlePadding={'0 0 0 16px'}
          titleBorderRadius={'8px'}
          isOpenByDefault={checkIfActive(level1)}
          hasBackgroundColor={false}
        >
          {level1.children?.map((level2) => (
            <Box key={level2.slug}>
              <Box padding={'11px 56px 8px 16px'}>
                <ArticleLink
                  slug={level1.slug}
                  subSlug={level2.slug}
                  title={level2.menuTitle}
                  isActive={activeSlug === level2.slug}
                  level={EAlternateLevels.LEVEL2}
                />
              </Box>

              {level2.children?.map((level3) => (
                <Box padding={'11px 56px 8px'} key={level3.slug}>
                  <ArticleLink
                    slug={level1.slug}
                    subSlug={level2.slug}
                    subSubSlug={level3.slug}
                    title={level3.menuTitle}
                    isActive={activeSlug === level3.slug}
                    level={EAlternateLevels.LEVEL3}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Accordion>
      ))}
    </Box>
  );
};
